/**
 * 121PASS Dashboard Admin - Professional Color System
 * Minimalist & Elegant Design
 */

// ============================================================================
// PRIMITIVE COLORS - Base palette
// ============================================================================

const primitive = {
  // Primary
  black: '#000000',
  white: '#FFFFFF',

  // Neutrals - Subtle gray scale for professional interfaces
  neutral: {
    50: '#FAFAFA',
    100: '#F5F5F5',
    200: '#E5E5E5',
    300: '#D4D4D4',
    400: '#A3A3A3',
    500: '#737373',
    600: '#525252',
    700: '#404040',
    800: '#262626',
    900: '#171717',
  },

  // Success - Professional emerald green
  success: {
    50: '#ECFDF5',
    100: '#D1FAE5',
    500: '#10B981',
    600: '#059669',
    700: '#047857',
  },

  // Error - Professional red
  error: {
    50: '#FEF2F2',
    100: '#FEE2E2',
    500: '#EF4444',
    600: '#DC2626',
    700: '#B91C1C',
  },
} as const;

// ============================================================================
// SEMANTIC TOKENS - Context-based colors
// ============================================================================

export interface ColorTokens {
  // Background
  background: {
    primary: string;
    secondary: string;
    tertiary: string;
  };

  // Foreground
  foreground: {
    primary: string;
    secondary: string;
    tertiary: string;
  };

  // Border
  border: {
    primary: string;
    secondary: string;
    focus: string;
  };

  // Brand
  brand: {
    primary: string;
    onPrimary: string;
  };

  // Interactive States
  interactive: {
    background: string;
    hover: string;
    active: string;
  };

  // Status
  status: {
    success: string;
    successSubtle: string;
    error: string;
    errorSubtle: string;
  };
}

// ============================================================================
// LIGHT THEME
// ============================================================================

export const lightTheme: ColorTokens = {
  background: {
    primary: primitive.white,
    secondary: primitive.neutral[50],
    tertiary: primitive.neutral[100],
  },

  foreground: {
    primary: primitive.black,
    secondary: primitive.neutral[600],
    tertiary: primitive.neutral[400],
  },

  border: {
    primary: primitive.neutral[200],
    secondary: primitive.neutral[100],
    focus: primitive.black,
  },

  brand: {
    primary: primitive.black,
    onPrimary: primitive.white,
  },

  interactive: {
    background: primitive.neutral[100],
    hover: primitive.neutral[200],
    active: primitive.neutral[300],
  },

  status: {
    success: primitive.success[500],
    successSubtle: primitive.success[50],
    error: primitive.error[500],
    errorSubtle: primitive.error[50],
  },
};

// ============================================================================
// DARK THEME
// ============================================================================

export const darkTheme: ColorTokens = {
  background: {
    primary: primitive.black,
    secondary: primitive.neutral[900],
    tertiary: primitive.neutral[800],
  },

  foreground: {
    primary: primitive.white,
    secondary: primitive.neutral[400],
    tertiary: primitive.neutral[500],
  },

  border: {
    primary: primitive.neutral[800],
    secondary: primitive.neutral[900],
    focus: primitive.white,
  },

  brand: {
    primary: primitive.white,
    onPrimary: primitive.black,
  },

  interactive: {
    background: primitive.neutral[800],
    hover: primitive.neutral[700],
    active: primitive.neutral[600],
  },

  status: {
    success: primitive.success[500],
    successSubtle: primitive.success[700],
    error: primitive.error[500],
    errorSubtle: primitive.error[700],
  },
};

// ============================================================================
// EXPORTS
// ============================================================================

export const colors = {
  primitive,
  light: lightTheme,
  dark: darkTheme,
} as const;
